import { useEffect, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'

/**
 * A dialog for the operator console.
 *
 * <p>Written here rather than pulled in because every destructive action in this console
 * needs one and the shared component library has no dialog. Small on purpose: a native
 * {@code <dialog>} does the focus trap, the backdrop and Escape, so there is no keyboard
 * behaviour to hand-roll and get subtly wrong.
 */
export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  footer,
}: {
  open: boolean
  title: string
  description?: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
}) {
  const ref = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={onClose}
      className="m-auto w-[min(32rem,calc(100vw-2rem))] rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-card)] p-0 text-[var(--color-body)] backdrop:bg-slate-900/50"
    >
      {open && (
        <form method="dialog" onSubmit={(event) => event.preventDefault()}>
          <div className="flex items-start gap-4 border-b border-[var(--color-border)] px-5 py-4">
            <div>
              <h2 className="text-base font-semibold text-[var(--color-heading)]">{title}</h2>
              {description && (
                <p className="mt-1 text-sm text-[var(--color-muted)]">{description}</p>
              )}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              aria-label="Close"
              className="ms-auto"
            >
              <X className="size-4" aria-hidden />
            </Button>
          </div>

          <div className="space-y-4 px-5 py-4">{children}</div>

          {footer && (
            <div className="flex justify-end gap-2 border-t border-[var(--color-border)] bg-[var(--color-page)] px-5 py-3">
              {footer}
            </div>
          )}
        </form>
      )}
    </dialog>
  )
}
